import React, { useState } from 'react';
import {
  View, StyleSheet, Image, Text, ScrollView, ImageStyle,
} from 'react-native';
import Modal from 'react-native-modal';
import { FontAwesome } from '@expo/vector-icons';
import { StackActions, useNavigation } from '@react-navigation/native';
import Moment from 'moment';
import {
  AssetCustomField, AssetInstance, Template, TemplateField, TemplateType,
} from '../../../../../constants/Types';
import { AppScreen } from '../../../../../navigation/LinkingConfiguration';
import Colors from '../../../../../constants/Colors';
import PressableStyled from '../../../../../components/PressableStyled';
import Style from '../../../../../constants/Style';
import { openInNewTab, showValueWithHyphenFallback } from '../../../../../constants/Utils';
import isMobile from '../../../../../components/IsMobile';
import LocationIcon16 from '../../../../../components/16pxIcons/LocationIcon16';
import { isPlacedOnMap } from '../../../Utils';
import { ADDITIONAL_FIELD_TYPES } from '../../../../../components/AssetManagement/AddTemplateModal/components/FieldList/components/FieldItem';
import CloseModalIcon from '../../../../../components/CloseModalIcon';
import ZoomIcon from '../../../../../components/24pxIcons/ZoomIcon';

const placeholderImage = require('../../../../../assets/images/image_placeholder.png');

interface OverviewProps {
  asset: AssetInstance,
  template: Template,
  showScreenAlert,
}

const Overview = ({ asset, template, showScreenAlert }: OverviewProps) => {
  const navigation = useNavigation();
  const [essentialsShown, setEssentialsShown] = useState<boolean>(!isMobile);
  const [datesShown, setDatesShown] = useState<boolean>(!isMobile);
  const [additionalInfoShown, setAdditionalInfoShown] = useState<boolean>(!isMobile);
  const [imageModalShown, setImageModalShown] = useState<boolean>(false);
  const assetLocated: boolean = isPlacedOnMap(asset);
  const linkRegex: RegExp = new RegExp(ADDITIONAL_FIELD_TYPES.LINK);

  const customFields: AssetCustomField[] = (template?.customfields ?? [])
    .map((templateField: TemplateField) => {
      let value: string = asset?.customFields
        .filter((field: AssetCustomField) => field.name === templateField.Name)[0]?.value;
      if (templateField.Format === ADDITIONAL_FIELD_TYPES.DATE && value) {
        value = Moment(value).format('DD/MM/YYYY');
      }
      return {
        name: templateField.Name,
        value: value?.toString().length > 0 ? value : null,
        format: templateField.Format,
      };
    });

  const dateFields: AssetCustomField[] = customFields
    .filter((field: AssetCustomField) => field.format === ADDITIONAL_FIELD_TYPES.DATE);
  const additionalInfoFields: AssetCustomField[] = customFields
    .filter((field: AssetCustomField) => field.format !== ADDITIONAL_FIELD_TYPES.DATE);

  const goToTemplate = () => {
    if (!template) {
      showScreenAlert('Error', 'The template used to create this asset has been deleted');
      return;
    }
    navigation.navigate(
      isMobile ? AppScreen.TEMPLATE_MANAGEMENT : AppScreen.TEMPLATE_MANAGEMENTV2 as any,
      { templateId: template.id },
    );
  };

  const goToMap = ({
    floor, area, space, assetId,
  }: { floor: string, area?: string, space?: string, assetId?: string }) => {
    const mapParams = {
      floor,
      area,
      space,
      assetId,
      locationId: asset.locationId,
      assetsView: asset.type === TemplateType.ASSET,
      sensorsView: asset.type === TemplateType.SENSOR || asset.type === TemplateType.METER,
    };

    const rootStackNavigation = (navigation as any).getParent?.('root-stack');

    if (isMobile && rootStackNavigation?.dispatch) {
      rootStackNavigation.dispatch(StackActions.push(AppScreen.MAP, mapParams));
      return;
    }

    if (isMobile && typeof (navigation as any).dispatch === 'function') {
      (navigation as any).dispatch(StackActions.push(AppScreen.MAP, mapParams));
      return;
    }

    navigation.navigate(AppScreen.MAP as any, mapParams);
  };

  const goToFloor: () => void = () => {
    goToMap({ floor: asset.locationFloor, assetId: asset.locationArea ? undefined : asset.id });
  };

  const goToArea: () => void = () => {
    goToMap({
      floor: asset.locationFloor,
      area: asset.locationArea,
      assetId: asset.locationSpace ? undefined : asset.id,
    });
  };

  const goToSpace: () => void = () => {
    goToMap({
      floor: asset.locationFloor,
      area: asset.locationArea,
      space: asset.locationSpace,
      assetId: asset.id,
    });
  };

  return <ScrollView
    style={styles.container}
    testID='asset-overview-modal'
  >
    <View style={styles.topSection}>
      <PressableStyled
        onPress={() => setImageModalShown(true)}
        disabled={!asset.imageUrl}
        maintainOpacityWhenDisabled
      >
        <Image
          style={styles.image as ImageStyle}
          source={asset.imageUrl ? { uri: asset.imageUrl } : undefined}
          defaultSource={placeholderImage}
        />
        {!!asset.imageUrl && <ZoomIcon style={styles.zoomIcon} />}
      </PressableStyled>
      <View>
        <Text style={styles.descriptionLabel}>{'Description'}</Text>
        <Text
          selectable
          style={styles.description}
        >
          {showValueWithHyphenFallback(asset.description)}
        </Text>
        <View style={styles.mapLocationIndication}>
          <LocationIcon16 fill={assetLocated ? Colors.light.hereworksGreen400 : undefined} />
          <Text style={styles.mapLocationIndicationText}>
            {assetLocated ? 'Located' : 'Not located'}
          </Text>
        </View>
        {[TemplateType.METER, TemplateType.SENSOR].includes(asset.type)
          && <Text style={styles.descriptionLabel}>
            {'Labels: '}
            <Text style={styles.description}>
              {asset.labels?.length > 0 ? asset.labels.join(', ') : 'None'}
            </Text>
          </Text>}
      </View>
    </View>
    <PressableStyled
      style={styles.sectionContainer}
      onPress={() => setEssentialsShown(true)}
      disabled={!isMobile || essentialsShown}
      maintainOpacityWhenDisabled
    >
      <PressableStyled
        style={[
          styles.sectionHeader, (!isMobile || essentialsShown) && styles.sectionHeaderHighlighted,
        ]}
        onPress={() => setEssentialsShown(!essentialsShown)}
      >
        <Text style={styles.sectionTitle}>{'Essentials'}</Text>
        <FontAwesome
          name={essentialsShown ? 'angle-up' : 'angle-down'}
          size={16}
          color={Colors.light.hereworksBlue500}
        />
      </PressableStyled>
      {essentialsShown && <>
        <View style={styles.sectionContent}>
          <View style={styles.field}>
            <Text style={styles.descriptionLabel}>{'ID'}</Text>
            <Text
              style={styles.fieldValue}
              selectable
            >
              {asset.id}
            </Text>
          </View>
          <View style={styles.field}>
            <Text style={styles.descriptionLabel}>{'Template'}</Text>
            <PressableStyled
              onPress={goToTemplate}
              testID='navigate-to-tempalate-asset-btn'
            >
              <Text
                style={styles.linkText}
                selectable
              >
                {asset.templateName}
              </Text>
            </PressableStyled>
          </View>
        </View>
        <View style={styles.sectionContent}>
          {asset.locationFloor && <View style={styles.field}>
            <Text style={styles.descriptionLabel}>{'Floor'}</Text>
            <PressableStyled
              onPress={goToFloor}
              testID='navigate-to-floor-asset-btn'
            >
              <Text
                style={styles.linkText}
                selectable
              >
                {showValueWithHyphenFallback(asset.locationFloor)}
              </Text>
            </PressableStyled>
          </View>}
          {asset.locationArea && <View style={styles.field}>
            <Text style={styles.descriptionLabel}>{'Area'}</Text>
            <PressableStyled onPress={goToArea}>
              <Text
                style={styles.linkText}
                selectable
              >
                {showValueWithHyphenFallback(asset.locationArea)}
              </Text>
            </PressableStyled>
          </View>}
          {asset.locationSpace && <View style={styles.field}>
            <Text style={styles.descriptionLabel}>{'Space'}</Text>
            <PressableStyled onPress={goToSpace}>
              <Text
                style={styles.linkText}
                selectable
              >
                {showValueWithHyphenFallback(asset.locationSpace)}
              </Text>
            </PressableStyled>
          </View>}
        </View>
        <View style={styles.sectionContent}>
          <View style={styles.field}>
            <Text style={styles.descriptionLabel}>{'Model'}</Text>
            <Text
              style={styles.fieldValue}
              selectable
            >
              {showValueWithHyphenFallback(asset.model)}
            </Text>
          </View>
          <View style={styles.field}>
            <Text style={styles.descriptionLabel}>{'Serial number'}</Text>
            <Text
              style={styles.fieldValue}
              selectable
            >
              {showValueWithHyphenFallback(asset.serial)}
            </Text>
          </View>
          <View style={styles.field}>
            <Text style={styles.descriptionLabel}>{'Manufacturer'}</Text>
            <Text
              style={styles.fieldValue}
              selectable
            >
              {showValueWithHyphenFallback(asset.manufacturer)}
            </Text>
          </View>
        </View>
      </>}
    </PressableStyled>
    <PressableStyled
      style={styles.sectionContainer}
      onPress={() => setDatesShown(true)}
      disabled={!isMobile || datesShown}
      maintainOpacityWhenDisabled
    >
      <PressableStyled
        style={[
          styles.sectionHeader, (!isMobile || datesShown) && styles.sectionHeaderHighlighted,
        ]}
        onPress={() => setDatesShown(!datesShown)}
      >
        <Text style={styles.sectionTitle}>{'Dates'}</Text>
        <FontAwesome
          name={datesShown ? 'angle-up' : 'angle-down'}
          size={16}
          color={Colors.light.hereworksBlue500}
        />
      </PressableStyled>
      {datesShown && <View style={styles.sectionContent}>
        {dateFields?.length > 0 ? dateFields.map((field: AssetCustomField) => <View
          style={styles.field}
          key={field.name}
        >
          <Text style={styles.descriptionLabel}>{field.name}</Text>
          <Text
            style={styles.fieldValue}
            selectable
          >
            {showValueWithHyphenFallback(field.value)}
          </Text>
        </View>) : <Text style={styles.noFieldsText}>
          {'No date fields available for this asset'}
        </Text>}
      </View>}
    </PressableStyled>
    <PressableStyled
      style={styles.sectionContainer}
      onPress={() => setAdditionalInfoShown(true)}
      disabled={!isMobile || additionalInfoShown}
      maintainOpacityWhenDisabled
    >
      <PressableStyled
        style={[
          styles.sectionHeader,
          (!isMobile || additionalInfoShown) && styles.sectionHeaderHighlighted,
        ]}
        onPress={() => setAdditionalInfoShown(!additionalInfoShown)}
      >
        <Text style={styles.sectionTitle}>{'Additional Info'}</Text>
        <FontAwesome
          name={additionalInfoShown ? 'angle-up' : 'angle-down'}
          size={16}
          color={Colors.light.hereworksBlue500}
        />
      </PressableStyled>
      {additionalInfoShown && <View style={styles.sectionContent}>
        {additionalInfoFields?.length > 0 ? additionalInfoFields.map((field: AssetCustomField) => {
          const isLink: boolean = linkRegex.test(field.value?.trim() ?? '');

          return <View
            style={styles.field}
            key={field.name}
          >
            <Text style={styles.descriptionLabel}>{field.name}</Text>
            <PressableStyled
              onPress={() => openInNewTab(field.value)}
              disabled={!isLink}
              maintainOpacityWhenDisabled
            >
              <Text
                style={[styles.fieldValue, isLink && styles.linkText]}
                selectable
              >
                {showValueWithHyphenFallback(field.value)}
              </Text>
            </PressableStyled>
          </View>;
        }) : <Text style={styles.noFieldsText}>
          {'No additional info fields available for this asset'}
        </Text>}
      </View>}
    </PressableStyled>
    <Modal
      isVisible={imageModalShown}
      animationIn='fadeIn'
      animationOut='fadeOut'
      onBackdropPress={() => setImageModalShown(false)}
    >
      <Image
        style={styles.imageLarge as ImageStyle}
        source={asset.imageUrl ? { uri: asset.imageUrl } : undefined}
        defaultSource={placeholderImage}
      />
      <PressableStyled
        style={[Style.closeButton, styles.closeButton]}
        onPress={() => setImageModalShown(false)}
      >
        <CloseModalIcon />
      </PressableStyled>
    </Modal>
  </ScrollView>;
};

const styles = StyleSheet.create({
  container: {
    marginHorizontal: -16,
    paddingHorizontal: 16,
    marginVertical: isMobile ? 12 : undefined,
    paddingVertical: isMobile ? 4 : undefined,
  },
  topSection: {
    paddingTop: isMobile ? 16 : 8,
    padding: isMobile ? 16 : undefined,
    flexDirection: isMobile ? 'column' : 'row',
    marginBottom: isMobile ? 8 : 24,
    borderRadius: 16,
    borderWidth: isMobile ? 1 : undefined,
    borderColor: Colors.light.hereworksBlue50,
    backgroundColor: '#FFF',
  },
  image: {
    width: isMobile ? '100%' : 138,
    aspectRatio: 1.45,
    height: isMobile ? undefined : 95,
    marginEnd: isMobile ? undefined : 16,
    borderWidth: isMobile ? undefined : 1,
    borderRadius: 8,
    borderColor: Colors.light.hereworksWhite300,
    backgroundColor: Colors.light.hereworksPrimaryGrey300,
    marginBottom: isMobile ? 8 : undefined,
  },
  imageLarge: {
    width: 650,
    aspectRatio: 1,
    borderRadius: 8,
    alignSelf: 'center',
  },
  descriptionLabel: {
    fontSize: 12,
    lineHeight: 14,
    color: Colors.light.hereworksBlack400,
    fontFamily: 'Roboto_400Regular',
    marginBottom: 4,
  },
  description: {
    fontSize: 14,
    lineHeight: 20,
    color: Colors.light.hereworksBlack600,
    fontFamily: 'Roboto_500Medium',
  },
  sectionContainer: {
    backgroundColor: '#FFF',
    borderRadius: 16,
    borderColor: Colors.light.hereworksBlue50,
    padding: isMobile ? 16 : undefined,
    borderWidth: isMobile ? 1 : undefined,
    marginBottom: isMobile ? 8 : undefined,
  },
  sectionHeader: {
    ...Style.row,
    paddingVertical: 4,
    borderBottomColor: Colors.light.hereworksBlue100,
  },
  sectionHeaderHighlighted: {
    borderBottomWidth: 1,
    marginBottom: 12,
  },
  sectionTitle: {
    fontSize: 12,
    lineHeight: 14,
    fontFamily: 'Roboto_500Medium',
    color: Colors.light.hereworksBlack400,
    textTransform: 'uppercase',
  },
  sectionContent: {
    flexDirection: isMobile ? 'column' : 'row',
    flexWrap: 'wrap',
    marginHorizontal: -16,
  },
  field: {
    paddingHorizontal: 16,
    marginBottom: 16,
    width: isMobile ? '100%' : '33%',
  },
  fieldValue: {
    fontSize: 14,
    lineHeight: 20,
    color: Colors.light.hereworksBlack600,
    fontFamily: 'Roboto_500Medium',
  },
  linkText: {
    fontSize: 14,
    lineHeight: 22,
    fontFamily: 'Roboto_500Medium',
    color: Colors.light.hereworksBlue500,
    textDecorationLine: 'underline',
  },
  noFieldsText: {
    marginHorizontal: 16,
    marginBottom: 24,
    fontSize: 13,
    lineHeight: 19,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack600,
  },
  mapLocationIndication: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: 8,
  },
  mapLocationIndicationText: {
    fontSize: 12,
    lineHeight: 16,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack600,
    marginStart: 4,
  },
  closeButton: {
    alignSelf: 'center',
    top: '50%',
    start: '50%',
    transform: [{ translateX: 341 }, { translateY: -325 }],
  },
  zoomIcon: {
    position: 'absolute',
    bottom: isMobile ? 18 : 6,
    end: isMobile ? 12 : 22,
    borderRadius: 3,
  },
});

export default Overview;
